<template>
    <b-row class="flex-grow-1">
        <b-col lg="auto" cols="12">
            <asset-preview :name="name"
                           :type="asset.asset_type"
                           :preview-url="previewUrl"
                           :thumb-url="thumbUrl"
                           :avatar-open="editing">
                <slot name="assetPreviewSlot"></slot>
            </asset-preview>

            <div class="text-center">
                <b-badge class="rounded font-weight-regular" :variant="statusBadgeVariant">
                    {{ statusBadgeText(status) }}
                </b-badge>
            </div>

            <div class="text-center" v-if="editing">
                <file-input accept="image/jpeg" label="CHANGE ASSET" v-model="file" class="mt-2"/>
                <a :href="assetUrl" target="_blank" class="btn btn-outline btn-sm btn-rounded mt-2">
                    DOWNLOAD ASSET
                </a>
            </div>
        </b-col>
        <b-col lg="" cols="12">
            <b-row>
                <b-col>
                    <b-row>
                        <b-col cols="6">
                            <b-form-group label="NAME" label-class="text-truncate">
                                <b-input type="text" v-model="name" :disabled="!editing"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="6">
                            <b-form-group label="REFERENCE NUMBER" label-class="text-truncate">
                                <b-input type="text" disabled :value="asset.reference_number"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <div v-show="editing" class="pb-2">
                        <b-row>
                            <b-col xl="6" lg="12">
                                <tag-select field-name="CATEGORIES"
                                            :tags="categories"
                                            :multiple="true"
                                            :select-picker-options="{maxOptions: 3}"
                                            v-model="selectedCategories"/>
                            </b-col>

                            <b-col xl="6" lg="12"
                                   v-for="(tagCategoryData, tagCategoryName) in tagStore.tagsByCategory">
                                <tag-select :field-name="tagCategoryName"
                                            :tags="tagCategoryData.tags"
                                            :multiple="true"
                                            :select-picker-options="{maxOptions: tagCategoryData.max_options}"
                                            v-model="selectedTags[tagCategoryName]"/>
                            </b-col>

                            <b-col xl="6" lg="12">
                                <b-form-group label="CAPTION" label-class="text-truncate">
                                    <b-input type="text" v-model="caption"/>
                                </b-form-group>
                            </b-col>

                            <b-col xl="6" lg="12">
                                <b-form-group label="COPYRIGHT" label-class="text-truncate">
                                    <b-input type="text" v-model="copyright"/>
                                </b-form-group>
                            </b-col>

                            <b-col xl="6" lg="12">
                                <b-form-group label="LOCATION" label-class="text-truncate">
                                    <b-input type="text" v-model="location"/>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <hr/>

                        <metadata :metadata="assetMetadata"/>

                        <hr/>

                        <b-row>
                            <b-col lg="6">
                                <b-form-group label="KEYWORDS" class="mt-2">
                                    <select2 v-model="keywords"
                                             :options="keywordOptions"
                                             :settings="{ tags: true, multiple: true, placeholder: 'Add Keywords', tokenSeparators: [',', ';'] }"/>
                                </b-form-group>
                            </b-col>
                            <b-col lg="6">
                                <tag-select field-name="STATUS"
                                            :tags="statusOptions"
                                            v-model="status"/>
                            </b-col>
                        </b-row>

                        <b-row>
                            <b-col lg="">
                                <b-form-group>
                                    <label class="text-truncate">NOTES</label>
                                    <b-textarea v-model="notes"/>
                                </b-form-group>
                            </b-col>
                            <b-col lg="">
                                <label class="text-truncate">RELEASE FORMS</label>
                            </b-col>
                        </b-row>


                        <b-row>
                            <b-col>
                                <b-button variant="link"
                                          class="p-0"
                                          @click="showActivityModal = true">
                                    Show All Activity
                                </b-button>

                                <b-modal title="Activity"
                                         v-model="showActivityModal"
                                         body-class="p-0"
                                         hide-footer
                                         size="lg"
                                         centered
                                         scrollable>
                                    <asset-versions :asset-id="asset.id"/>
                                </b-modal>
                            </b-col>
                        </b-row>

                        <b-row v-if="errors">
                            <b-col>
                                <ul class="text-danger">
                                    <li v-for="error in errors">
                                        {{ error }}
                                    </li>
                                </ul>
                            </b-col>
                        </b-row>
                    </div>

                    <EditReleaseForms :asset="asset"
                                      v-model="releaseForms"
                                      :initial-release-forms="submissionReleaseForms"
                                      @releaseRequiredChanged="releaseRequired = arguments[0]"
                                      @releaseFormsChanged="submissionReleaseForms = arguments[0]"
                                      :release-required="releaseRequired"
                                      :release-form-url="releaseFormUrl"
                                      accepted-files="application/pdf"/>
                </b-col>

                <b-col cols="12" lg="auto">
                    <card-options
                            :editing="editing"
                            :saving="saving"
                            :expanded="expanded"
                            @editing="handleCardEditingTrigger"
                            @saveAsset="handleAssetSave"/>
                </b-col>
            </b-row>
        </b-col>
    </b-row>
</template>

<script>
  import startCase from 'lodash/startCase'
  import ReleaseFormList from '../../ReleaseForms/ReleaseFormList'
  import TagSelect from '../../Common/TagSelect'
  import AssetPreview from '../../Asset/AssetPreview'
  import CardOptions from '../../Asset/CardOptions'
  import Metadata from '../../Asset/AssetMetadata'
  import AssetMixins from "../../../mixins/AssetMixins"
  import generalMixins from "../../../mixins/GeneralMixins"
  import FileInput from "../../Common/FileInput"
  import AssetVersions from "./AssetVersions";
  import select2 from 'v-select2-component';
  import EditReleaseForms from "./EditReleaseForms";
  import PreventNavigationMixins from "../../../mixins/PreventNavigationMixins";

  export default {
    props: ['asset', 'url', 'tagStore', 'categories', 'expanded', 'shouldPreventSaveLoss', 'submissionReleaseForms', 'releaseFormUrl'],

    data: function () {
      return {
        editing: this.expanded,
        saving: false,
        pendingChanges: false,
        name: this.asset.name || "",
        location: this.asset.location || "",
        copyright: this.asset.copyright || "",
        caption: this.asset.caption || "",
        selectedCategories: this.asset.category_ids || [],
        selectedTags: this.getSelectedTags(this.asset, this.tagStore),
        keywords: this.asset.keywords || [],
        keywordOptions: this.asset.keywords || [],
        status: this.asset.status,
        notes: this.asset.notes,
        file: null,
        thumbUrl: this.asset.thumb_url,
        assetUrl: this.asset.asset_url,
        mediaThumbUrl: this.asset.media_thumb_url,
        assetMetadata: this.asset.metadata,
        errors: null,
        showActivityModal: false,
        releaseForms: this.asset.release_forms || [],
        releaseRequired: true
      }
    },

    methods: {
      updateWithAsset: function (asset) {
        this.$emit('assetUpdate', asset);
        this.updateData('name', asset.name);
        this.updateData('location', asset.location);
        this.updateData('copyright', asset.copyright);
        this.updateData('caption', asset.caption);
        this.updateData('selectedCategories', asset.category_ids || []);
        this.updateData('selectedTags', this.getSelectedTags(asset, this.tagStore));
        this.updateData('keywords', asset.keywords || []);
        this.updateData('status', asset.status);
        this.updateData('notes', asset.notes);
        this.updateData('thumbUrl', asset.thumb_url);
        this.updateData('mediaThumbUrl', asset.media_thumb_url);
        this.updateData('assetUrl', asset.asset_url);
        this.updateData('assetMetadata', asset.metadata);
        this.updateData('releaseRequired', asset.release_required);
        this.updateData('releaseForms', asset.release_forms || []);
      },

      updateData: function (dataAttributeName, value) {
        if (value !== undefined) {
          this[dataAttributeName] = value;
        }
      },

      statusBadgeText: function (status) {
        if (status === 'pending') {
          return 'Pending Review'
        } else {
          return startCase(status);
        }
      },

      handleCardEditingTrigger: function () {
        if (!this.expanded) {
          this.editing = !this.editing
        }
      },

      handleAssetSave: function () {
        this.saveAsset(this.assetFormData, !this.expanded).then(response => {
          if (response.status < 400) {
            this.showToast('Asset was updated', 'Asset Edit');
          } else {
            this.errors = this.getErrorMessages(response.body);
          }
          this.file = null;

          this.pendingChanges = false;
          this.$emit('pendingChanges', false);
        })
      }
    },

    mounted: function () {
      if (this.shouldPreventSaveLoss) {
        this.preventSaveLoss(() => {
          return this.pendingChanges;
        });
      }
    },

    watch: {
      dataValues: function (val) {
        if (this.editing) {
          this.pendingChanges = true;
          this.$emit('pendingChanges', true);
        }
      },

      saving: function (value) {
        if (value) {
          this.errors = null;
        }
      }
    },

    computed: {
      previewUrl() {
        if (this.assetType === "Illustration") {
          return this.mediaThumbUrl
        } else {
          return this.assetUrl
        }
      },

      dataValues: function () {
        return this.name,
          this.selectedCategories,
          this.selectedTags,
          this.keywords,
          this.notes,
          this.location,
          this.caption,
          this.copyright,
          this.releaseRequired,
          this.releaseForms,
          Date.now();
      },

      statusBadgeVariant: function () {
        return {
          approved: 'success',
          rejected: 'danger',
          changes_requested: 'warning',
          pending: 'primary',
          buyout: 'warning',
          deactivated: 'danger'
        }[this.status];
      },

      statusOptions: function () {
        return ['pending', 'approved', 'rejected', 'changes_requested', /*'buyout',*/ 'deactivated'].map(el => {
          return {
            id: el,
            name: startCase(el)
          }
        })
      },

      assetFormData: function () {
        return {
          name: this.name,
          location: this.location,
          copyright: this.copyright,
          caption: this.caption,
          category_ids: this.formDataArray(this.selectedCategories),
          tag_ids: this.formDataArray(this.formSelectedTags),
          keywords: this.formDataArray(this.keywords),
          status: this.status,
          notes: this.notes,
          data: this.file,
          release_form_ids: this.formDataArray(this.releaseForms.map(el => el.id)),
          release_required: this.releaseRequired
        };
      }
    },

    mixins: [AssetMixins, generalMixins, PreventNavigationMixins],

    components: {
      ReleaseFormList,
      TagSelect,
      AssetPreview,
      CardOptions,
      Metadata,
      FileInput,
      AssetVersions,
      EditReleaseForms,
      select2
    }
  }
</script>